import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Xmark, ArrowsRotateRight } from '@gravity-ui/icons';
import NumberedPagination from '../../../components/NumberedPagination/NumberedPagination';
import DistributionDonut from '../../../components/DistributionDonut/DistributionDonut';
import {
  countSelectedBusinessEvents,
  getBusinessEvent,
  listBusinessEvents,
} from '../../../services/businessAnalytics';
import { buildCountSelectedPayload } from '../../ViolationsPage/components/eventsStatsModel';
import { getStatusBadge } from '../../../constants/violationConstants';
import { useCameraNames } from '../../../hooks/useCameraNames';
import { formatDuration } from '../../../utils/formatDuration';
import { formatShortInTimezone } from '../../../utils/formatInTimezone';
import { formatEventCondition } from '../../../utils/ruleFormat';
import { withCameraWord } from '../../../utils/hierarchyLabels';
import { describeUserError } from '../../../utils/apiError';
import { countRu, EVENT_FORMS } from '../../../utils/pluralRu';
import SummaryStateBlock from './SummaryStateBlock';

/*
 * Бизнес-события правила из карточки вкладки «Бизнес-метрики»
 * (BM-FE-TZ-002C). Это не violation events и ими не подменяется: список —
 * GET /business/events, карточка — GET /business/events/{id}, «Посчитать» —
 * POST /business/events/count-selected с тем же фильтром.
 *
 * Фильтр — rule_id и камера карточки плюс весь период вкладки (не
 * backend-drilldown за 24 часа). 404 по карточке — событие сменило категорию
 * вместе с правилом: показываем это, а не общий сбой.
 */

const PAGE_SIZE = 25;

/** Параметры списка: только заполненные поля, одиночные ID. */
export const buildBusinessEventsQuery = (drilldown) => Object.fromEntries(
  Object.entries({
    rule_id: drilldown?.ruleId,
    camera_id: drilldown?.cameraId,
    from: drilldown?.from,
    to: drilldown?.to,
  }).filter(([, value]) => value != null && value !== '')
);

const eventRange = (event, timezone) => {
  const tz = event.display_timezone || timezone || undefined;
  const start = formatShortInTimezone(event.started_at, tz);
  const end = event.ended_at ? formatShortInTimezone(event.ended_at, tz) : null;
  return end ? `${start}–${end}` : start;
};

const eventDuration = (event) => {
  if (!event.started_at || !event.ended_at) return null;
  const seconds = (new Date(event.ended_at) - new Date(event.started_at)) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

const DetailRow = ({ label, children }) => (
  <div className="sum-bev-detail-row">
    <div className="sum-bev-detail-label">{label}</div>
    <div className="sum-bev-detail-value">{children}</div>
  </div>
);

const EventDetail = ({ eventId, cameraLabel, cameraTimezone, onBack, onRefreshList }) => {
  const [state, setState] = useState({ status: 'loading', event: null, error: '' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', event: null, error: '' });
    getBusinessEvent(eventId)
      .then((event) => { if (!cancelled) setState({ status: 'ready', event, error: '' }); })
      .catch((error) => {
        if (cancelled) return;
        setState({
          status: error?.response?.status === 404 ? 'gone' : 'error',
          event: null,
          error: describeUserError(error, 'Не удалось открыть бизнес-событие'),
        });
      });
    return () => { cancelled = true; };
  }, [eventId, reloadToken]);

  const back = (
    <button type="button" className="sum-link sum-link--button sum-bev-back" onClick={onBack}>
      <ChevronLeft width={16} height={16} aria-hidden />
      К списку
    </button>
  );

  if (state.status === 'loading') return <>{back}<SummaryStateBlock state="loading" /></>;
  if (state.status === 'gone') {
    return (
      <>
        {back}
        <SummaryStateBlock
          state="error"
          message="Событие больше недоступно в этой вкладке. Обновите список, чтобы увидеть актуальные события."
          onReset={onRefreshList}
          resetLabel="Обновить список"
          resetIcon={<ArrowsRotateRight width={16} height={16} />}
        />
      </>
    );
  }
  if (state.status === 'error') {
    return (
      <>
        {back}
        <SummaryStateBlock state="error" message={state.error} onRetry={() => setReloadToken((value) => value + 1)} />
      </>
    );
  }

  const { event } = state;
  const tz = event.display_timezone || cameraTimezone(event.camera_id) || undefined;
  const duration = eventDuration(event);
  const condition = formatEventCondition(event);
  return (
    <>
      {back}
      <div className="sum-bev-detail">
        <DetailRow label="Правило">{event.rule_name || '—'}</DetailRow>
        {condition && condition !== '—' && <DetailRow label="Условие">{condition}</DetailRow>}
        <DetailRow label="Камера">{event.camera_id ? cameraLabel(event.camera_id) : '—'}</DetailRow>
        <DetailRow label="Начало">{formatShortInTimezone(event.started_at, tz)}</DetailRow>
        {event.confirmed_at && (
          <DetailRow label="Подтверждено">{formatShortInTimezone(event.confirmed_at, tz)}</DetailRow>
        )}
        {event.ended_at && <DetailRow label="Окончание">{formatShortInTimezone(event.ended_at, tz)}</DetailRow>}
        {duration != null && <DetailRow label="Длительность">{formatDuration(duration)}</DetailRow>}
        <DetailRow label="Статус">{getStatusBadge(event.status).label}</DetailRow>
      </div>
    </>
  );
};

const CountPanel = ({ query }) => {
  const [state, setState] = useState({ status: 'idle', result: null, error: '' });
  const queryKey = JSON.stringify(query);

  // Другой фильтр — прежнее число относится к другому набору событий.
  useEffect(() => { setState({ status: 'idle', result: null, error: '' }); }, [queryKey]);

  const calculate = () => {
    setState({ status: 'loading', result: null, error: '' });
    countSelectedBusinessEvents(buildCountSelectedPayload({ mode: 'filters', filters: JSON.parse(queryKey) }))
      .then((result) => setState({ status: 'ready', result, error: '' }))
      .catch((error) => setState({
        status: 'error',
        result: null,
        error: describeUserError(error, 'Не удалось посчитать бизнес-события'),
      }));
  };

  return (
    <section className="sum-bev-count" aria-label="Статистика по бизнес-событиям">
      <div className="sum-bev-count-head">
        {state.result && (
          <h3 className="sum-bev-count-title">{`Всего ${countRu(state.result.selected_count, EVENT_FORMS)}`}</h3>
        )}
        <button
          type="button"
          className="sum-btn"
          onClick={calculate}
          disabled={state.status === 'loading'}
        >
          {state.status === 'loading' ? 'Считаем…' : (state.result ? 'Пересчитать' : 'Посчитать')}
        </button>
      </div>
      {state.status === 'error' && <p className="sum-muted">{state.error}</p>}
      {state.result && (
        <div className="sum-donuts">
          <DistributionDonut
            title="По названиям правил"
            distribution={state.result.rule_distribution}
            unitForms={EVENT_FORMS}
            emptyText="Нет данных"
          />
          <DistributionDonut
            title="По метрикам"
            distribution={state.result.metric_distribution}
            unitForms={EVENT_FORMS}
            emptyText="Нет данных"
          />
        </div>
      )}
    </section>
  );
};

const BusinessEventsDrawer = ({ drilldown, onClose }) => {
  const [offset, setOffset] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const [openEventId, setOpenEventId] = useState(null);
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const query = useMemo(() => buildBusinessEventsQuery(drilldown), [drilldown]);

  useEffect(() => { setOffset(0); setOpenEventId(null); }, [drilldown]);

  useEffect(() => {
    const controller = new AbortController();
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    listBusinessEvents({ ...query, limit: PAGE_SIZE, offset }, { signal: controller.signal })
      .then((data) => setState({ status: 'ready', data, error: '' }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          data: null,
          error: describeUserError(error, 'Не удалось загрузить бизнес-события'),
        });
      });
    return () => controller.abort();
  }, [query, offset, reloadToken]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = useMemo(() => state.data?.items || [], [state.data]);
  const { cameraLabel, cameraTimezone } = useCameraNames(items);
  const refreshList = useCallback(() => {
    setOpenEventId(null);
    setReloadToken((value) => value + 1);
  }, []);

  const title = [drilldown?.ruleName, drilldown?.cameraName && withCameraWord(drilldown.cameraName)]
    .filter(Boolean).join(' · ');

  const renderList = () => {
    if (state.status === 'error') {
      return <SummaryStateBlock state="error" message={state.error} onRetry={() => setReloadToken((value) => value + 1)} />;
    }
    if (!state.data) return <SummaryStateBlock state="loading" />;
    if (items.length === 0) return <SummaryStateBlock state="empty" message="За период бизнес-событий нет" />;
    return (
      <>
        <CountPanel query={query} />
        <div className="sum-bev-scroll">
          <table className="sum-bev-table">
            <thead>
              <tr>
                <th>Время</th>
                <th>Длительность</th>
                <th>Камера</th>
                <th>Правило</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody aria-busy={state.status === 'loading'}>
              {items.map((event) => {
                const duration = eventDuration(event);
                return (
                  <tr key={event.id} className="sum-bev-row" onClick={() => setOpenEventId(event.id)}>
                    <td>
                      <button type="button" className="sum-link sum-link--button" onClick={(click) => { click.stopPropagation(); setOpenEventId(event.id); }}>
                        {eventRange(event, cameraTimezone(event.camera_id))}
                      </button>
                    </td>
                    <td>{duration == null ? '—' : formatDuration(duration)}</td>
                    <td>{event.camera_id ? cameraLabel(event.camera_id) : '—'}</td>
                    <td>{event.rule_name || '—'}</td>
                    <td>{getStatusBadge(event.status).label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <NumberedPagination
          total={state.data.total}
          limit={PAGE_SIZE}
          offset={offset}
          onOffsetChange={setOffset}
          disabled={state.status === 'loading'}
        />
      </>
    );
  };

  return (
    <>
      <div className="sum-drawer-overlay" onClick={onClose} />
      <aside className="sum-drawer" role="dialog" aria-label="Бизнес-события">
        <div className="sum-drawer-header">
          <div>
            <h2 className="sum-drawer-title">Бизнес-события</h2>
            {title && <div className="sum-muted">{title}</div>}
          </div>
          <button type="button" className="sum-drawer-close" onClick={onClose} aria-label="Закрыть" title="Закрыть">
            <Xmark width={16} height={16} aria-hidden />
          </button>
        </div>
        <div className="sum-drawer-body">
          {openEventId != null ? (
            <EventDetail
              eventId={openEventId}
              cameraLabel={cameraLabel}
              cameraTimezone={cameraTimezone}
              onBack={() => setOpenEventId(null)}
              onRefreshList={refreshList}
            />
          ) : renderList()}
        </div>
      </aside>
    </>
  );
};

export default BusinessEventsDrawer;
